import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useContext,
  useRef,
} from 'react';
import { Spin, message } from 'antd';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../store';
import { useParams, useNavigate, usePathname } from '../../navigation';
import { LoadingIcon } from '../components/Spinner';
import { RootState } from '../../rootReducer';
import store from '../../store';
import NavBar from '../components/BackButton';
import { getAnalytics, logEvent } from 'firebase/analytics';
import * as Sentry from '@sentry/react';
import { setUserProfile } from '../../features/auth/auth';
import {
  createOrderObj,
  calculateCartCheck,
  clearCart,
  setPaymentCreate,
  setOrderCreate,
  changeOrderType,
} from '../../features/cart/cart';
import ErrorIcon from '../components/ErrorIcon';
import { ordersApi } from '../../api/order';
import { fetchOrder, setOrder } from '../../features/order/order';
import { getRestaurant } from '../../features/restaurant/restaurant';
import { guestApi } from '../../api/guest';
import TrackingContext from '../Providers/TrackingContext';
import SHA256 from 'crypto-js/sha256';
import PaymentSheet from './components/PaymentWrapper';

// Import our extracted components
import { CartItemList } from './components/CartItemList';
import EmailRequired from './components/EmailRequired';
import TableNumberRequired from './components/TableNumberRequired';
import OrderErrorPopUp from './components/OrderErrorPopUp';
import CheckInfoCard from './components/Check';
import TipCollection from './components/TipCollection';
import PromotionField from './components/PromotionField';
import TableTabBanner from '../components/TableTabBanner';
import { TableNumberConfirmationPopUp } from './components/TableNumberConfirmationPopUp';
import { CateringOrderDetails } from './components/CateringOrderDetails';
import { PickupOrderDetails } from './components/PickupOrderDetails';
import { DeliveryOrderDetails } from './components/DeliveryOrderDetails';
import { HotelOrderDetails } from './components/HotelOrderDetails';
import { STSOrderDetails } from './components/STSOrderDetails';
import { SQLoyaltyCheck } from '../Loyalty/SQLoyaltyCheck';
import { SubscriptionManagementMessage } from './components/SubscriptionManagementMessage';
import { getPrice, getModifierPairingTotal } from './components/DishCard';
import { CartHeader } from './components/CartHeader';
import { OrderTypeSwitch } from './components/OrderTypeSwitch';
import { OrderDetailsSection } from './components/OrderDetailSection';
import { OrderActionButton } from './components/OrderActionButton';
import { DishEditDrawer } from './components/DishEditDrawer';
import { TTUserProfile } from '../../interfaces/user';
import {
  TTOrderCreate,
  TTOrderV2,
  TTOrderV2Create,
} from '../../interfaces/order';
import { TTRestaurantProfile } from '../../interfaces/restaurant';
import { TTOrderItemCreate } from '../../interfaces/order_item';
import { TTCheck } from '../../interfaces/check';

const Cart = () => {
  // State hooks
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [placingOrder, setPlacingOrder] = useState(false);
  const [showEmailNeeded, setShowEmailNeeded] = useState(false);
  const [showTableNumberNeeded, setShowTableNumberNeeded] = useState(false);
  const [confirmTableNumber, setConfirmTableNumber] = useState(false);
  const [preSetTip, setPreSetTip] = useState(null);
  const [editDishId, setEditDishId] = useState(null);
  const [registerForLoyalty, setRegisterForLoyalty] = useState(false);
  const [showPaymentSheet, setShowPaymentSheet] = useState(false);
  const [existingPaymentIntent, setExistingPaymentIntent] = useState(null);

  // Refs
  const payment_btn_ref = useRef<HTMLDivElement>(null);
  const analytics = getAnalytics();

  // Redux selectors
  const guest = useSelector<RootState, TTUserProfile | null>(
    state => state.auth.userProfile,
  );
  const order = useSelector<RootState, TTOrderV2 | null>(
    state => state.order.order,
  );
  const restaurant_profile = useSelector<RootState, TTRestaurantProfile | null>(
    state => state.restaurant.profile,
  );
  const cart = useSelector<RootState, TTOrderV2Create | null>(
    state => state.cart.order_create,
  );
  const cart_items = useSelector<RootState, TTOrderItemCreate[]>(
    state => state.cart.cart_items,
  );
  const cart_merchants = useSelector<RootState, number[]>(
    state => state.cart.cart_merchants,
  );

  const cartCheck = useSelector<RootState, TTCheck>(state => state.cart.check);
  const selected_pm = useSelector<RootState, string | null>(
    state => state.payment.selected_pm,
  );
  const email = useSelector<RootState, string | null>(
    state => state.auth.userProfile?.email ?? null,
  );

  const total = useSelector<RootState, number>(state => {
    return (
      state.cart.check.total +
      (state.order.guest_check != null ? state.order.guest_check.total : 0) +
      (state.cart.check.service_fee ?? 0)
    );
  });

  // Router hooks
  const { r_id } = useParams();
  const pathname = usePathname();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  // Context
  const { trackPageView, trackFBEvent, trackGAEvent } = useContext(
    TrackingContext,
  );

  // Memoized values
  const rId = useMemo(() => parseInt(r_id), [r_id]);

  // Check if this is a free order (subtotal = 0)
  const isFreeOrder = useMemo(() => {
    return (cartCheck?.sub_total || 0) <= 0;
  }, [cartCheck]);

  // Callbacks
  const scrollToTop = useCallback(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  }, []);

  const scrollPaymentBtnInView = useCallback(() => {
    if (payment_btn_ref.current) {
      // @ts-ignore
      payment_btn_ref?.current?.scrollIntoView({ behavior: 'smooth' });
      navigate(pathname, { replace: true });
    }
  }, [navigate, pathname]);

  const switchOrderType = useCallback(async () => {
    try {
      setLoading(true);
      dispatch(
        changeOrderType(cart.order_type === 'dine_in' ? 'pick_up' : 'dine_in'),
      );
      setLoading(false);
    } catch (e) {
      setLoading(false);
      message.error(e.message);
    }
  }, [cart?.order_type, dispatch]);

  const checkPromo = useCallback(
    async promoId => {
      try {
        setLoading(true);
        // Promo check logic...
        setLoading(false);
      } catch (err) {
        console.error(err.response);
        message.error({ content: 'Invalid promotion', icon: <ErrorIcon /> });
        setLoading(false);
      }
    },
    [dispatch],
  );

  // Calculator functions
  const calculateTotalAmount = useCallback((cartState, orderState) => {
    return (
      cartState.check.total +
      (orderState.guest_check != null ? orderState.guest_check.total : 0)
    );
  }, []);

  const calculateTrackValue = useCallback(orderItems => {
    return (
      orderItems
        ?.map(i => {
          return getPrice(i, getModifierPairingTotal(i)) / 100.0;
        })
        ?.reduce((a, b) => a + b, 0) / 100.0
    );
  }, []);

  const buildOrderItems = useCallback(orderItems => {
    return orderItems?.map(i => {
      return {
        quantity: i.quantity,
        id: `prod_${i.menu_item_id}`,
        name: i.name,
        price: getPrice(i, getModifierPairingTotal(i)) / 100.0,
      };
    });
  }, []);

  // Place order with support for free items
  const placeOrder = useCallback(
    async (is_wallet = false, ev) => {
      scrollToTop();
      setPlacingOrder(true);

      const cartState = store.getState().cart;
      const orderState = store.getState().order;
      const restaurantProfile = store.getState().restaurant.profile;
      const authState = store.getState().auth;

      try {
        // Create or update email if necessary
        if (email && email !== guest.email) {
          const resp = await guestApi.updateGuest(
            { email: email },
            authState.guest_id,
          );
          dispatch(setUserProfile(resp.data));
        }

        // For free orders, we don't need payment intent
        let payment_intent_payload = null;

        // if (!isFreeOrder) {
        // Create payment intent for paid orders
        const totalAmount = calculateTotalAmount(cartState, orderState);
        const paymentMethodId = is_wallet
          ? restaurantProfile.processing_platform === 'STRIPE'
            ? ev.paymentMethod.id
            : ev
          : selected_pm;

        payment_intent_payload = {
          processing_platform: restaurantProfile.processing_platform,
          restaurant_ids: [restaurantProfile.restaurant_id],
          guest_id: authState.userProfile.id,
          total_amount: totalAmount,
          currency: 'USD',
          restaurant_id: restaurantProfile.restaurant_id,
          receipt_address: email ?? 'none',
          is_wallet: is_wallet,
          payment_method_id: paymentMethodId,
          order_id: order?.id,
          table_number: cartState.order_create.table_number,
          tip_amount: cartState.check.tip,
        };
        // }
        // Update order state with payment info and free order flag
        dispatch(
          setOrderCreate({
            ...cartState.order_create,
            payment_intent_id: existingPaymentIntent?.payment_intent_id,
            // is_free_order: isFreeOrder
          }),
        );
        // Create or update order
        let orderResponse;

        if (
          orderState?.order !== null &&
          !orderState.order?.is_single_session
        ) {
          // Update existing order
          orderResponse = await ordersApi.updateOrder(orderState.order_id, {
            ...cartState.order_create,
            // @ts-ignore
            payment_create: payment_intent_payload,
            payment_id: existingPaymentIntent?.payment_intent_id,
            // is_free_order: isFreeOrder
          });
        } else {
          // Create new order
          let orderData = {
            ...cartState.order_create,
            payment_create: payment_intent_payload,
            payment_id: existingPaymentIntent?.payment_intent_id,
          };

          // Add additional data if available
          if (Object.keys(cartState.fulfillment_details ?? {}).length !== 0) {
            orderData.order_fulfillment_create = cartState.fulfillment_details;
          }

          if (cartState.qr_code !== null) {
            orderData.qr_code = cartState.qr_code;
          }

          orderResponse = await ordersApi.createOrder(orderData);
        }

        // Handle successful wallet payment
        if (is_wallet && restaurantProfile.processing_platform === 'STRIPE') {
          ev.complete('success');
        }

        // Track analytics events
        const trackValue = calculateTrackValue(
          cartState.order_create?.order_items,
        );
        const orderItems = buildOrderItems(cartState.order_create?.order_items);

        trackFBEvent(
          'Purchase',
          {
            value: trackValue,
            num_items: cartState.order_create?.order_items?.length ?? 0,
            currency: 'USD',
            contents: orderItems,
          },
          false,
          orderResponse?.data?.restaurant_ids,
        );

        trackGAEvent(
          'purchase',
          {
            currency: 'USD',
            value: trackValue,
            items: orderItems,
            transaction_id: SHA256(orderResponse?.data?.created_at),
          },
          orderResponse?.data?.restaurant_ids,
        );

        // Log event
        logEvent(analytics, 'order_placed', {
          merchant_id: rId,
          order_id: orderResponse?.data?.id,
          is_free_order: isFreeOrder,
        });

        // Update state with new order
        dispatch(setOrder(orderResponse.data));

        // Clear cart and navigate to order page
        dispatch(clearCart(rId));
        navigate(`/feed/restaurants/${rId}/order/${orderResponse.data.id}`);
      } catch (err) {
        // Handle wallet payment failure
        if (is_wallet && restaurantProfile.processing_platform === 'STRIPE') {
          ev.complete('fail');
        }

        setPlacingOrder(false);
        Sentry.captureException(err);

        // Handle specific error cases
        if (err.response?.data) {
          if (
            err.response.data.detail ===
            'Order has been closed. Please enter your current table number, and try again.'
          ) {
            setConfirmTableNumber(true);
            return;
          }

          setError(
            err?.response?.data?.detail[0]?.msg ??
              err?.response?.data?.detail ??
              "Unable to submit the order. Please don't refresh the page or cancel during processing.",
          );
        } else {
          setError(
            "Unable to process the order. Please don't refresh the page or cancel during processing.",
          );
        }
      }
    },
    [
      scrollToTop,
      isFreeOrder,
      email,
      guest?.email,
      dispatch,
      calculateTotalAmount,
      selected_pm,
      order?.id,
      calculateTrackValue,
      buildOrderItems,
      trackFBEvent,
      trackGAEvent,
      analytics,
      rId,
      navigate,
      existingPaymentIntent,
    ],
  );

  // Handler to validate and place order (free or paid)
  const handlePlaceOrder = useCallback(() => {
    // Validate cart has items
    if (cart_items.length === 0) {
      message.error({
        content: 'Empty cart, please add items!',
        icon: <ErrorIcon />,
      });
      return;
    }

    // Validate table number
    if (
      cart.order_type === 'dine_in' &&
      (!cart.table_number ||
        !restaurant_profile.table_numbers.includes(cart.table_number))
    ) {
      setShowTableNumberNeeded(true);
      return;
    }

    // Validate email
    if (!guest.email && (!email || !/\S+@\S+\.\S+/.test(email))) {
      setShowEmailNeeded(true);
      return;
    }

    if (isFreeOrder) {
      // Free order - place directly
      placeOrder(false, null);
    } else {
      // Paid order - use existing payment intent or show payment sheet
      if (order?.id && order.is_open && existingPaymentIntent) {
        dispatch(
          setOrderCreate({
            ...cart,
            payment_intent_id: existingPaymentIntent.payment_intent_id,
          }),
        );
        placeOrder(false, null);
      } else {
        setShowPaymentSheet(true);
      }
    }
  }, [
    cart_items.length,
    cart?.order_type,
    cart?.table_number,
    restaurant_profile?.table_numbers,
    guest?.email,
    email,
    isFreeOrder,
    order?.id,
    order?.is_open,
    existingPaymentIntent,
    dispatch,
    placeOrder,
  ]);

  // useEffect hooks

  // Fetch open payment intent for existing order
  useEffect(() => {
    const fetchOpenPaymentIntent = async () => {
      if (order && order.id && order.is_open) {
        try {
          const response = await ordersApi.getOpenPaymentIntent(order.id);
          if (response.data) {
            setExistingPaymentIntent(response.data);
          }
        } catch (error) {
          console.error('Failed to fetch open payment intent:', error);
        }
      }
    };

    fetchOpenPaymentIntent();
  }, [order?.id]);

  // Initialize empty order state
  useEffect(() => {
    if (store.getState().cart.order_create === null) {
      dispatch(createOrderObj());
    }
  }, [dispatch]);

  // Fetch restaurant profile
  useEffect(() => {
    let isMounted = true;

    const fetchRestaurantProfile = async () => {
      dispatch(
        getRestaurant(rId, () => {
          if (!isMounted) return;

          const profile = store.getState().restaurant.profile;

          if (profile.is_fixed_grat) {
            setPreSetTip(profile.fixed_grat);
            // Update tip percent in Redux...
          }
        }),
      );
    };

    if (restaurant_profile === null) {
      fetchRestaurantProfile();
    } else if (restaurant_profile.is_fixed_grat) {
      setPreSetTip(restaurant_profile.fixed_grat);
      // Update tip percent in Redux...
    }

    return () => {
      isMounted = false;
    };
  }, [dispatch, restaurant_profile, rId]);

  // Load existing order if needed
  useEffect(() => {
    const orderId = parseInt(localStorage.getItem('__tt_order_id'));

    if (guest !== null && !isNaN(orderId) && order === null) {
      dispatch(
        fetchOrder(orderId, () => {
          console.log('got order!');
        }),
      );
    }
  }, [guest?.id, order, dispatch]);

  // Calculate cart check
  useEffect(() => {
    if (restaurant_profile !== null) {
      dispatch(calculateCartCheck());
    }
  }, [dispatch, restaurant_profile]);

  // Log analytics for cart view
  useEffect(() => {
    logEvent(analytics, 'cart_view');
  }, [analytics]);

  return (
    <div className="overflow-y-auto scrolling-touch min-h-screen bg-surface">
      <Spin
        spinning={placingOrder}
        indicator={LoadingIcon}
        size="large"
        tip="Processing..."
      >
        <NavBar show_refresh={false} hide={true} />
        <Spin
          spinning={loading}
          indicator={LoadingIcon}
          className="h-96"
          wrapperClassName="md:mx-8 lg:mx-16 xl:mx-32"
        >
          {/* Cart Header */}
          <CartHeader
            order={order}
            restaurant_profile={restaurant_profile}
            onViewExistingOrder={() =>
              navigate(`/feed/restaurants/${r_id}/order/${order.id}`)
            }
          />

          {loading && cart === null ? (
            <main
              className={`flex-1 relative z-0 overflow-y-auto ${
                loading ? 'pt-20' : 'pt-0'
              }`}
            />
          ) : (
            cart !== null &&
            restaurant_profile !== null &&
            guest !== null && (
              <div>
                {/* Order Type Switch */}
                {isNaN(parseInt(localStorage.getItem('__tt_order_id'))) &&
                  restaurant_profile.is_pickup &&
                  cart.order_type === 'dine_in' && (
                    <OrderTypeSwitch
                      orderType={cart.order_type}
                      onSwitch={switchOrderType}
                      isLoading={loading}
                    />
                  )}

                {/* Cart Item List */}
                {cart.order_items !== null && (
                  <CartItemList
                    items={cart.order_items}
                    onEditItem={setEditDishId}
                    onAddMore={() =>
                      navigate(
                        `/feed/restaurants/${restaurant_profile.restaurant_id}/menus`,
                      )
                    }
                    onClearCart={() => {
                      dispatch(clearCart());
                      message.success('Cart cleared!');
                      navigate(
                        `/feed/restaurants/${restaurant_profile.restaurant_id}/menus`,
                      );
                    }}
                  />
                )}

                {/* Order Fulfillment Details */}
                {order === null &&
                  cart_merchants?.map((c, i) => {
                    // Render different components based on order type
                    if (cart.order_type === 'catering') {
                      return (
                        <CateringOrderDetails
                          key={c}
                          restaurant_id={c?.toString()}
                        />
                      );
                    } else if (cart.order_type === 'delivery') {
                      return (
                        <DeliveryOrderDetails
                          key={c}
                          restaurant_id={c?.toString()}
                        />
                      );
                    } else if (cart.order_type === 'hotel') {
                      return (
                        <HotelOrderDetails
                          key={c}
                          restaurant_id={c?.toString()}
                        />
                      );
                    } else if (cart.order_type === 'sts') {
                      return (
                        <STSOrderDetails
                          key={c}
                          restaurant_id={c?.toString()}
                          show_overlap_attributes={i === 0}
                        />
                      );
                    } else if (
                      cart.order_type === 'pick_up' ||
                      cart.order_type === 'take_out'
                    ) {
                      return (
                        <PickupOrderDetails
                          key={c}
                          restaurant_id={c?.toString()}
                        />
                      );
                    }
                    return null;
                  })}

                {/* Tip Collection */}
                <TipCollection preset={preSetTip !== null} tip={preSetTip} />

                {/* Promotion Field */}
                {!restaurant_profile?.config?.hide_promo && (
                  <PromotionField
                    onClick={async promo => {
                      logEvent(analytics, 'promo_check', { promo_id: promo });
                      await checkPromo(promo);
                    }}
                  />
                )}

                <div className="h-0.5 w-4/5 bg-outline-variant my-4 mx-auto" />

                {/* Check Info (Subtotal, Tax, etc.) */}
                {guest !== null && <CheckInfoCard />}

                {(isNaN(parseInt(localStorage.getItem('__tt_order_id'))) ||
                  guest?.email === null) && (
                  <div className="h-0.5 w-4/5 bg-outline-variant my-4 mx-auto" />
                )}

                {/* Order Details Section */}
                <OrderDetailsSection
                  orderType={cart.order_type}
                  onSwitchOrderType={switchOrderType}
                  onUpdateEmail={async email => {
                    logEvent(analytics, 'set_email', { email: email });
                    setLoading(true);
                    try {
                      const resp = await guestApi.updateGuest(
                        { email: email },
                        guest.id,
                      );
                      dispatch(setUserProfile(resp.data));
                      setLoading(false);
                    } catch (err) {
                      console.log(email);
                      setLoading(false);
                      Sentry.captureException(err);
                    }
                  }}
                  isLoading={loading}
                />

                {/* Loyalty Registration */}
                {restaurant_profile?.processing_platform === 'SQUARE' && (
                  <div className="mx-4 my-4 pb-4">
                    <SQLoyaltyCheck
                      merchant_ids={
                        restaurant_profile?.connected_vendor_ids?.concat(
                          restaurant_profile.restaurant_id,
                        ) ?? [restaurant_profile?.restaurant_id]
                      }
                      onCheck={val => {
                        setRegisterForLoyalty(val);
                      }}
                    />
                  </div>
                )}

                <div className="h-0.5 w-4/5 bg-gray-900 my-4 mx-auto" />

                {/* Subscription Message */}
                {cart?.order_items?.filter(
                  i => i.product_type === 'subscription',
                )?.length !== 0 && <SubscriptionManagementMessage />}

                {/* Order Action Button */}
                <div id="payment-btn" ref={payment_btn_ref}>
                  <OrderActionButton
                    isFreeOrder={isFreeOrder}
                    isExistingOrder={
                      order !== null && !order?.is_single_session
                    }
                    restaurant_profile={restaurant_profile}
                    disabled={cart_items.length === 0 || placingOrder}
                    isLoading={placingOrder}
                    onClick={handlePlaceOrder}
                  />
                </div>

                {/* Table Tab Banner */}
                <div className="w-full">
                  <TableTabBanner />
                </div>

                {/* Payment Sheet */}
                <PaymentSheet
                  isOpen={showPaymentSheet}
                  onClose={() => setShowPaymentSheet(false)}
                  onSuccess={async payment_intent => {
                    setShowPaymentSheet(false);
                    if (payment_intent) {
                      dispatch(
                        setOrderCreate({
                          ...store.getState().cart.order_create,
                          payment_intent_id: payment_intent.id,
                        }),
                      );
                      setPlacingOrder(true);
                      scrollToTop();
                      await placeOrder(false, null);
                    }
                  }}
                  amount={Math.round(total)}
                  merchantIds={cart_merchants}
                  platformFee={cartCheck.service_fee ?? 0}
                  guestId={guest.id}
                  description={`Payment to ${restaurant_profile.name}`}
                  stripeAccountId={restaurant_profile.stripe_account_id}
                  currency={restaurant_profile.currency ?? 'usd'}
                  region={restaurant_profile.server_region}
                  unique_time={
                    cart.order_items
                      ?.map(i => i.cart_insert_ts)
                      .reduce((a, b) => a + b, 0) ?? 0
                  }
                />

                {/* Dish Edit Drawer */}
                <DishEditDrawer
                  isOpen={editDishId !== null}
                  onClose={() => setEditDishId(null)}
                  dishItem={editDishId}
                />

                {/* Modals */}
                {!loading && showEmailNeeded && (
                  <EmailRequired callback={() => setShowEmailNeeded(false)} />
                )}

                {!loading && showTableNumberNeeded && (
                  <TableNumberRequired
                    callback={() => setShowTableNumberNeeded(false)}
                  />
                )}

                {!loading && error !== null && (
                  <OrderErrorPopUp
                    callback={() => {
                      setError(null);
                      window.location.reload();
                    }}
                    error={error}
                  />
                )}

                {!loading && confirmTableNumber && (
                  <TableNumberConfirmationPopUp
                    callback={async () => {
                      setError(null);
                      window.location.reload();
                    }}
                  />
                )}
              </div>
            )
          )}
        </Spin>
      </Spin>
    </div>
  );
};

export default Cart;
